import { MapPin, StickyNote, UserRound } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FieldRow, FormSection, Input, Textarea } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useTranslation } from '@platform/i18n';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormSheet } from '../FormSheet.tsx';
import { AreaFields } from '../geography/AreaFields.tsx';
import { useGeoAreas } from '../geography/useGeoAreas.ts';
import { useApiErrorMessage } from '../useApiError.ts';
import {
  CUSTOMER_LIMITS,
  customerBodyOf,
  customerFormSchema,
  formValuesOf,
  NO_PREFILL,
  refusedFieldOf,
  type CustomerField,
  type CustomerPrefill,
  type CustomerFormValues,
} from './customer-form.ts';
import { phoneHolderOf, type Customer, type CustomerBody } from './customers.ts';

export type CustomerSheetProps = {
  api: TypedClient;
  editing: Customer | null;
  prefill?: CustomerPrefill;
  submitting: boolean;
  error: unknown;
  onOpenHolder: (customerId: string) => void;
  onClose: () => void;
  onSubmit: (body: CustomerBody) => void;
};

const SERVER_ERROR = 'server';

const REFUSAL_KEYS: Partial<Record<CustomerField, string>> = { phone: 'customers.phoneInvalid' };

export const CustomerSheet = ({
  api,
  editing,
  prefill = NO_PREFILL,
  submitting,
  error,
  onOpenHolder,
  onClose,
  onSubmit,
}: CustomerSheetProps) => {
  const { t } = useTranslation();
  const describe = useApiErrorMessage();
  const geography = useGeoAreas(api).data;
  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: formValuesOf(editing, prefill),
    mode: 'onBlur',
  });
  const { errors } = form.formState;
  const phoneHolderId = phoneHolderOf(error);
  const refusedField = refusedFieldOf(error);
  const unexplained = phoneHolderId === null && refusedField === null ? (describe(error)?.message ?? null) : null;

  useEffect(() => {
    const field = refusedFieldOf(error);
    if (field !== null) form.setError(field, { type: SERVER_ERROR }, { shouldFocus: true });
  }, [error, form]);

  const errorTextOf = (field: CustomerField, invalidKey: string): string | undefined => {
    const fieldError = errors[field];
    if (fieldError === undefined) return undefined;
    return t(fieldError.type === SERVER_ERROR ? (REFUSAL_KEYS[field] ?? 'customers.fieldInvalid') : invalidKey);
  };
  const phoneTaken = phoneHolderId === null ? undefined : t('customers.phoneTaken');
  const phoneError = errorTextOf('phone', 'customers.phoneRequired') ?? phoneTaken;

  return (
    <FormSheet
      open
      formId="customer-form"
      title={editing ? t('customers.editCustomer') : t('customers.newCustomer')}
      submitting={submitting}
      errorMessage={unexplained}
      onClose={onClose}
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          onSubmit(customerBodyOf(values));
        })(event);
      }}
    >
      <FormSection
        variant="plain"
        title={t('customers.identitySection')}
        description={t('customers.identityHint')}
        icon={<UserRound size={18} />}
      >
        <Input
          label={t('customers.name')}
          required
          autoComplete="off"
          maxLength={CUSTOMER_LIMITS.name}
          errorText={errorTextOf('name', 'customers.nameRequired')}
          {...form.register('name')}
        />
        <FieldRow>
          <Input
            label={t('customers.phone')}
            required
            type="tel"
            inputMode="tel"
            autoComplete="off"
            dir="ltr"
            maxLength={CUSTOMER_LIMITS.phone}
            helperText={t('customers.phoneHelp')}
            errorText={phoneError}
            {...form.register('phone')}
          />
          <Input
            label={t('customers.secondPhone')}
            type="tel"
            inputMode="tel"
            autoComplete="off"
            dir="ltr"
            maxLength={CUSTOMER_LIMITS.phone}
            errorText={errorTextOf('secondPhone', 'customers.fieldInvalid')}
            {...form.register('secondPhone')}
          />
        </FieldRow>
        {phoneHolderId === null ? null : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onOpenHolder(phoneHolderId);
            }}
          >
            {t('customers.openExisting')}
          </Button>
        )}
        <Input
          label={t('customers.email')}
          type="email"
          autoComplete="off"
          maxLength={CUSTOMER_LIMITS.email}
          errorText={errorTextOf('email', 'customers.emailInvalid')}
          {...form.register('email')}
        />
      </FormSection>
      <FormSection
        variant="plain"
        title={t('customers.addressSection')}
        description={t('customers.addressHint')}
        icon={<MapPin size={18} />}
      >
        <Textarea
          label={t('customers.address')}
          maxLength={CUSTOMER_LIMITS.address}
          rows={2}
          errorText={errorTextOf('address', 'customers.fieldInvalid')}
          {...form.register('address')}
        />
        <AreaFields
          form={form}
          geography={geography}
          by="name"
          governorate="governorate"
          delegation="city"
          labels={{ governorate: t('customers.governorate'), delegation: t('customers.city') }}
        />
        <Input
          label={t('customers.postalCode')}
          inputMode="numeric"
          autoComplete="off"
          maxLength={CUSTOMER_LIMITS.postalCode}
          errorText={errorTextOf('postalCode', 'customers.fieldInvalid')}
          {...form.register('postalCode')}
        />
      </FormSection>
      <FormSection variant="plain" title={t('customers.notesSection')} icon={<StickyNote size={18} />}>
        <Textarea
          label={t('customers.notes')}
          hideLabel
          helperText={t('customers.notesHelp')}
          maxLength={CUSTOMER_LIMITS.notes}
          errorText={errorTextOf('notes', 'customers.fieldInvalid')}
          {...form.register('notes')}
        />
      </FormSection>
    </FormSheet>
  );
};
